var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/default-param-last */
import React from 'react';
import * as Yup from 'yup';
// import Geocode from 'react-geocode';
import { setKey, setLanguage, fromAddress } from 'react-geocode';
import { useSnackbar } from 'notistack';
import { Button, Grid } from '@mui/material';
import isoCountries from 'i18n-iso-countries';
import isoCountriesEn from 'i18n-iso-countries/langs/en.json';
import API from '../../../graphql/createClient';
import { createAddress, updateAddress } from '../../../graphql/mutations';
import FormDropDown from '../formDropDown/FormDropDown';
import FormField from '../formField/FormField';
import GoogleMapComponent from '../../render/googleMap/GoogleMap';
import { checkLatLng, covertLatLngToGeometry } from '../../../helpers/googleMapFunctions';
import { convertCoordinateToLatLng, convertLatLngToCoordinate, convertLatLngToPoint } from '../../../helpers/googleMapHelper';
import './AddressForm.scss';
isoCountries.registerLocale(isoCountriesEn);
const countries = Object.values(isoCountries.getNames('en', { select: 'official' }));
export const addressInitialValues = {
    address_line_1: '',
    address_line_2: '',
    city: '',
    state_province: '',
    country: '',
    country_code: '',
    postal_code: '',
    geo: '',
};
export const addressValidations = Yup.object()
    .nullable()
    .shape({
    geo: Yup.string().test('geo-check', 'Invalid GPS Coordinates', (value) => !value || checkLatLng(value)),
});
export const addressRequiredValidations = Yup.object()
    .nullable()
    .shape({
    country: Yup.string().nullable().required('Please enter a country for this address'),
    city: Yup.string().nullable().required('Please enter a city for this address'),
    geo: Yup.string().test('geo-check', 'Invalid GPS Coordinates', (value) => !value || checkLatLng(value)),
});
export const addressIsEmpty = (address = {}) => !address ||
    (!address.country && !address.address_line_1 && !address.address_line_2 && !address.city && !address.state_province && !address.postal_code && !address.geo);
export function onSubmitAddress() {
    return __awaiter(this, arguments, void 0, function* (address = {}, { setFieldError, setFieldTouched }, name = 'address') {
        function create(input) {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    const res = yield API.graphql({ query: createAddress, variables: { input } });
                    if (res.data && res.data.createAddress) {
                        return res.data.createAddress.id;
                    }
                    return false;
                }
                catch (error) {
                    console.log('error creating address: ', error);
                }
            });
        }
        function update(input) {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    const res = yield API.graphql({ query: updateAddress, variables: { input } });
                    if (res.data && res.data.updateAddress) {
                        return res.data.updateAddress.id;
                    }
                    return false;
                }
                catch (error) {
                    console.log('error updating address: ', error);
                }
            });
        }
        try {
            let hasError = false;
            setFieldTouched(`${name}`, {
                country: true,
                city: true,
                geo: true,
            });
            if (!(address === null || address === void 0 ? void 0 : address.country)) {
                setFieldError(`${name}.country`, 'Please enter a country for this address');
                hasError = true;
            }
            if (!(address === null || address === void 0 ? void 0 : address.city)) {
                setFieldError(`${name}.city`, 'Please enter a city for this address');
                hasError = true;
            }
            if (!!(address === null || address === void 0 ? void 0 : address.geo) && !checkLatLng(address === null || address === void 0 ? void 0 : address.geo)) {
                setFieldError(`${name}.geo`, 'Invalid GPS Coordinates');
                hasError = true;
            }
            if (hasError) {
                return false;
            }
            const input = {
                country: address.country && address.country,
                country_code: address.country && isoCountries.getAlpha2Code(address.country, 'en'),
            };
            input.address_line_1 = address.address_line_1 ? address.address_line_1 : null;
            input.address_line_2 = address.address_line_2 ? address.address_line_2 : null;
            input.city = address.city ? address.city : null;
            input.state_province = address.state_province ? address.state_province : null;
            input.postal_code = address.postal_code ? address.postal_code : null;
            input.geo = address.geo ? covertLatLngToGeometry(address.geo) : null;
            if (address.id) {
                input.id = address.id;
                return yield update(input);
            }
            return yield create(input);
        }
        catch (err) {
            console.log('error creating/updating address', err);
        }
    });
}
export default function AddressForm({ name = 'address', setFieldValue, values, googleMapApi, mapCenter }) {
    const addressData = values[name] || {};
    const snackbar = useSnackbar();
    const { enqueueSnackbar } = snackbar;
    setKey(googleMapApi);
    setLanguage('en');
    const handleGeoChange = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { value = '' } = e.target;
        setFieldValue(`${name}.geo`, value);
    };
    const handleFindAddress = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        try {
            let address = addressData.address_line_1;
            if (addressData.address_line_2)
                address += (address ? ', ' : '') + addressData.address_line_2;
            if (addressData.city)
                address += (address ? ', ' : '') + addressData.city;
            if (addressData.state_province)
                address += (address ? ', ' : '') + addressData.state_province;
            if (addressData.postal_code)
                address += (address ? ', ' : '') + addressData.postal_code;
            if (addressData.country)
                address += (address ? ', ' : '') + addressData.country;
            const res = yield fromAddress(address);
            setFieldValue(`${name}.geo`, convertLatLngToCoordinate((_b = (_a = res === null || res === void 0 ? void 0 : res.results[0]) === null || _a === void 0 ? void 0 : _a.geometry) === null || _b === void 0 ? void 0 : _b.location));
            // eslint-disable-next-line @typescript-eslint/naming-convention, no-unsafe-optional-chaining
            const { location_type } = (_c = res === null || res === void 0 ? void 0 : res.results[0]) === null || _c === void 0 ? void 0 : _c.geometry;
            if (location_type === 'ROOFTOP') {
                enqueueSnackbar('Address coordinates found!', { variant: 'success' });
            }
            else if (['RANGE_INTERPOLATED', 'GEOMETRIC_CENTER', 'APPROXIMATE'].includes(location_type)) {
                enqueueSnackbar('Address coordinates approximated. Please refine on map.', { variant: 'warning' });
            }
            else {
                enqueueSnackbar('Address coordinates not found. Please select on map.', { variant: 'error' });
            }
        }
        catch (error) {
            enqueueSnackbar('Address coordinates not found. Please select on map.', { variant: 'error' });
        }
    });
    return (React.createElement(Grid, { container: true, spacing: 3 },
        React.createElement(Grid, { item: true, xs: 6, "data-offset": 6, "data-qa": "country" },
            React.createElement(FormDropDown, { data: countries, label: "Country*", name: `${name}.country` })),
        React.createElement(Grid, { item: true, xs: 6, "data-qa": "address_line_1" },
            React.createElement(FormField, { name: `${name}.address_line_1`, label: "Address Line 1" })),
        React.createElement(Grid, { item: true, xs: 6, "data-qa": "address_line_2" },
            React.createElement(FormField, { name: `${name}.address_line_2`, label: "Address Line 2" })),
        React.createElement(Grid, { item: true, xs: 6, "data-qa": "city" },
            React.createElement(FormField, { name: `${name}.city`, label: "City*" })),
        React.createElement(Grid, { item: true, xs: 3, "data-qa": "state_province" },
            React.createElement(FormField, { name: `${name}.state_province`, label: "State / Province" })),
        React.createElement(Grid, { item: true, xs: 3, "data-qa": "postal_code" },
            React.createElement(FormField, { name: `${name}.postal_code`, label: "Postal Code" })),
        React.createElement(Grid, { item: true, xs: 12 },
            React.createElement("p", { className: "map-instruction" }, "Click the map at the center of the new location you are creating and the coordinates will update accordingly. Conversely, input your desired coordinates and the map pin will update.")),
        React.createElement(Grid, { className: "geo-field-container", item: true, xs: 12 },
            React.createElement(FormField, { type: "geo", name: `${name}.geo`, label: "GPS Coordinates", onChange: handleGeoChange, onKeyPress: (e) => {
                    if (e.key === 'Enter') {
                        handleGeoChange(e);
                    }
                } }),
            React.createElement(Button, { className: "find-address", disabled: !addressData.city, variant: "contained", color: "primary", onClick: handleFindAddress }, "Find address")),
        React.createElement(Grid, { item: true, xs: 12 },
            React.createElement(GoogleMapComponent, { api_key: googleMapApi, mapCenter: convertCoordinateToLatLng(checkLatLng(addressData === null || addressData === void 0 ? void 0 : addressData.geo) ? addressData.geo : mapCenter), locations: checkLatLng(addressData === null || addressData === void 0 ? void 0 : addressData.geo) ? [{ geo: convertLatLngToPoint(convertCoordinateToLatLng(addressData.geo)) }] : [], setMarker: (latLng) => setFieldValue(`${name}.geo`, convertLatLngToCoordinate(latLng)), height: 460, showLabel: false }))));
}
